import { Club, Contact } from '@models';

/**
 * Normalized billing counterparty used by the manual invoice flow.
 *
 * Field names match **exactly** the ones the Handlebars template
 * `apps/functions/src/assets/templates/manual-invoice/manual-invoice.html`
 * expects (`contact.name`, `contact.nif`, `contact.address.street`, ...), so
 * both a `Contact` and a `Club` can be rendered by the same template without
 * touching it.
 *
 * Address fields are always present as strings (empty when unknown) so the
 * `{{#if}}` blocks of the template keep behaving as they did when only
 * contacts were supported.
 */
export interface BillingParty {
  name: string;
  nif: string;
  email?: string;
  phone?: string;
  address: {
    street: string;
    city: string;
    province: string;
    postalCode: string;
  };
}

/** Maps a `Contact` (fiscal fields: `nif`, `phone`, `address.street`, `address.postalCode`). */
export function contactToBillingParty(contact: Contact): BillingParty {
  return {
    name: contact.name ?? '',
    nif: contact.nif ?? '',
    email: contact.email,
    phone: contact.phone,
    address: {
      street: contact.address?.street ?? '',
      city: contact.address?.city ?? '',
      province: contact.address?.province ?? '',
      postalCode: contact.address?.postalCode ?? '',
    },
  };
}

/** Maps a `Club` (fiscal fields: `cif` → `nif`, `tel` → `phone`, `address.address` → `street`, `address.zipCode` → `postalCode`). */
export function clubToBillingParty(club: Club): BillingParty {
  return {
    name: club.name ?? '',
    nif: club.cif ?? '',
    email: club.email,
    phone: club.tel,
    address: {
      street: club.address?.address ?? '',
      city: club.address?.city ?? '',
      province: club.address?.province ?? '',
      postalCode: club.address?.zipCode ?? '',
    },
  };
}

/**
 * Resolves a billing counterparty by id looking **first in contacts and then in
 * clubs**. Firestore ids are random, so a single id column (`ManualInvoice.contactId`)
 * is unambiguous and needs no collection discriminator.
 *
 * Returns `null` when the id belongs to neither collection.
 */
export function resolveBillingParty(
  id: string,
  contacts: Contact[],
  clubs: Club[],
): BillingParty | null {
  const contact = contacts?.find((c) => c.id === id);
  if (contact) return contactToBillingParty(contact);

  const club = clubs?.find((c) => c.id === id);
  if (club) return clubToBillingParty(club);

  return null;
}
